import React, { useEffect, useState, lazy, createElement } from 'react';
import { FadeIn } from './components/FadeIn';
import { CTAButton } from './components/CTAButton';
import { FAQAccordion } from './components/FAQAccordion';
import { LegalModal, PrivacyContent, TermsContent } from './components/LegalModal';
import { Brain, Zap, Battery, CheckCircle2, XCircle, Clock, ShieldCheck, Sparkles, FileText, Users, Command, Power, Moon } from 'lucide-react';
export function App() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  useEffect(() => {
    // Manual Meta Pixel initialization
    const w = window as any;
    if (!w.fbq) {
      const n = (w.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      }) as any;
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [] as any[];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    w.fbq('init', '1084170331445397');
    w.fbq('track', 'PageView');
  }, []);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-dark text-gray-100 selection:bg-cyan selection:text-dark" data-id="element-0">
      {/* HERO SECTION */}
      <section className="relative overflow-hidden pt-24 pb-32 lg:pt-32 lg:pb-40" data-id="element-1">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-cyan/10 blur-[120px] rounded-full pointer-events-none" data-id="element-2" />

        <div className="container mx-auto px-6 relative z-10" data-id="element-3">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-4">
            <div className="flex-1 text-center lg:text-left" data-id="element-5">
              <FadeIn data-id="element-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-dark-card border border-dark-border mb-8" data-id="element-7">
                  <span className="flex h-2 w-2 rounded-full bg-cyan animate-pulse" data-id="element-8"></span>
                  <span className="text-sm font-medium text-gray-300" data-id="element-9">
                    The ADHD 'Brain-Sync' System
                  </span>
                </div>
              </FadeIn>

              <FadeIn delay={0.1} data-id="element-10">
                <h1 className="text-5xl lg:text-7xl font-display font-bold leading-tight mb-6" data-id="element-11">
                  Stop fighting your <span className="text-cyan" data-id="element-12">brain</span>.
                </h1>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-13">
                <p className="text-xl lg:text-2xl text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0" data-id="element-14">
                  Start using AI as your external prefrontal cortex to end
                  executive dysfunction for good.
                </p>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-15">
                <div className="flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start" data-id="element-16">
                  <CTAButton onClick={scrollToPricing} data-id="element-17">
                    GET THE BRAIN-SYNC SYSTEM NOW
                  </CTAButton>
                </div>
              </FadeIn>
            </div>

            <div className="flex-1 w-full max-w-lg lg:max-w-none" data-id="element-18">
              <FadeIn direction="left" delay={0.4} data-id="element-19">
                <div className="relative rounded-2xl overflow-hidden border border-dark-border bg-dark-card shadow-2xl shadow-cyan/5" data-id="element-20">
                  <div className="absolute inset-0 bg-gradient-to-t from-dark/80 to-transparent z-10" data-id="element-21" />
                  <img src="https://cdn.magicpatterns.com/uploads/er7ajSneAW5w9rUmuLv34N/BRAIN_-_SYNC.png" alt="The Brain-Sync System Mockup" className="w-full h-auto object-cover relative z-0" data-id="element-22" />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* THE HOOK & PROBLEM */}
      <section className="py-24 bg-dark-card/50 border-y border-dark-border" data-id="element-23">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-24">
          <FadeIn data-id="element-25">
            <div className="prose prose-invert prose-lg mx-auto" data-id="element-26">
              <p className="text-2xl font-display font-medium leading-relaxed text-center mb-16" data-id="element-27">
                You've been staring at the same to-do list for three hours. The
                guilt is heavy in your chest, but the{' '}
                <span className="text-cyan" data-id="element-28">"Wall of Awful"</span> between you
                and the first task feels a hundred feet high.
              </p>

              <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-29">
                <div data-id="element-30">
                  <h3 className="text-2xl font-display font-bold mb-4 flex items-center gap-3" data-id="element-31">
                    <Brain className="text-cyan h-6 w-6" data-id="element-32" />
                    You aren't lazy.
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-33">
                    You aren't unmotivated. Your brain is simply stuck in a
                    dopamine-deficit loop that traditional planners were never
                    built to fix.
                  </p>
                </div>
                <div data-id="element-34">
                  <h3 className="text-2xl font-display font-bold mb-4 flex items-center gap-3" data-id="element-35">
                    <Zap className="text-cyan h-6 w-6" data-id="element-36" />
                    The ADHD Tax
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-37">
                    For those of us with ADHD or chronic executive dysfunction,
                    "just getting started" is the hardest part of the day. We
                    spend more energy <em data-id="element-38">worrying</em> about tasks than
                    actually <em data-id="element-39">doing</em> them.
                  </p>
                </div>
              </div>

              <div className="mt-12 p-8 rounded-2xl bg-dark border border-dark-border text-center" data-id="element-40">
                <p className="text-gray-300 m-0" data-id="element-41">
                  We forget the milk, we agonize over simple emails, and we
                  crash into a "Hyperfocus Hangover" every evening because we
                  don't know how to transition out of work mode. This costs us
                  our time, our money, and our self-esteem.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24" data-id="element-42">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-43">
          <FadeIn data-id="element-44">
            <div className="text-center mb-16" data-id="element-45">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-46">
                Why what you tried{' '}
                <span className="text-red-400" data-id="element-47">didn't work</span>
              </h2>
              <p className="text-xl text-gray-400" data-id="element-48">
                The system isn't broken, it just wasn't built for your brain.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-49">
            {[{
            title: 'Traditional Planners',
            desc: "Feel like a prison sentence because they don't account for your shifting energy levels."
          }, {
            title: 'Willpower',
            desc: 'Is a finite battery that leaks by 11:00 AM, leaving you stranded for the rest of the day.'
          }, {
            title: '"Eat the Frog"',
            desc: 'Methods fail because for an ADHD brain, the frog looks like a dragon you need to hide from.'
          }, {
            title: 'Habit Trackers',
            desc: 'Become just another thing you feel guilty about failing after three days.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-50">
                <div className="p-8 rounded-2xl bg-dark-card border border-dark-border h-full flex items-start gap-4" data-id="element-51">
                  <XCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-1" data-id="element-52" />
                  <div data-id="element-53">
                    <h4 className="text-xl font-display font-bold mb-2" data-id="element-54">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 leading-relaxed" data-id="element-55">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 bg-cyan/5 border-y border-cyan/10 relative overflow-hidden" data-id="element-56">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-full bg-cyan/5 blur-[100px] rounded-full pointer-events-none" data-id="element-57" />

        <div className="container mx-auto px-6 max-w-4xl relative z-10 text-center" data-id="element-58">
          <FadeIn data-id="element-59">
            <Sparkles className="h-12 w-12 text-cyan mx-auto mb-6" data-id="element-60" />
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-8" data-id="element-61">
              The Micro-Dopamine Architecture
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-62">
              Instead of fighting your brain chemistry, the Brain-Sync System
              uses AI (ChatGPT or Claude) to act as an{' '}
              <strong className="text-white" data-id="element-63">external prefrontal cortex</strong>
              .
            </p>
            <p className="text-lg text-gray-400 leading-relaxed mb-12" data-id="element-64">
              It breaks "mountain-sized" projects into 3-minute micro-wins that
              trigger immediate dopamine. It bridges the gap between your
              creative ideas and the boring execution, giving you a clear,
              frictionless script for your day.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24" data-id="element-65">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-66">
          <FadeIn data-id="element-67">
            <div className="text-center mb-16" data-id="element-68">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-69">
                What You Get
              </h2>
              <p className="text-xl text-gray-400" data-id="element-70">
                Everything you need to bypass executive dysfunction.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-71">
            {[{
            icon: FileText,
            title: 'The Brain-Sync Protocol PDF',
            desc: 'The full guide to ending the "Wall of Awful" and syncing your focus.'
          }, {
            icon: Users,
            title: 'Digital Body Double Framework',
            desc: 'How to turn AI into a 24/7 non-judgmental accountability partner.'
          }, {
            icon: Command,
            title: '50-Prompt Command Center',
            desc: 'A copy-paste matrix to automate emails, meal plans, and life admin.'
          }, {
            icon: Power,
            title: 'Emergency "Start" Checklist',
            desc: "A 60-second ritual for those high-anxiety days when you can't move."
          }, {
            icon: Moon,
            title: 'Shutdown Secretary Protocol',
            desc: 'A transition tool to end the workday and protect your sleep.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className={i === 4 ? 'md:col-span-2 lg:col-span-1' : ''} data-id="element-72">
                <div className="p-8 rounded-2xl bg-dark-card border border-dark-border h-full hover:border-cyan/30 transition-colors group" data-id="element-73">
                  <div className="h-12 w-12 rounded-xl bg-dark border border-dark-border flex items-center justify-center mb-6 group-hover:bg-cyan/10 group-hover:border-cyan/30 transition-colors" data-id="element-74">
                    <item.icon className="h-6 w-6 text-cyan" data-id="element-75" />
                  </div>
                  <h4 className="text-xl font-display font-bold mb-3" data-id="element-76">
                    {item.title}
                  </h4>
                  <p className="text-gray-400 leading-relaxed" data-id="element-77">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn className="text-center" data-id="element-78">
            <CTAButton onClick={scrollToPricing} data-id="element-79">GET THE SYSTEM NOW</CTAButton>
          </FadeIn>
        </div>
      </section>

      {/* WHO IS THIS FOR / NOT FOR */}
      <section className="py-24 bg-dark-card/30 border-y border-dark-border" data-id="element-80">
        <div className="container mx-auto px-6 max-w-6xl" data-id="element-81">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-82">
            <FadeIn data-id="element-83">
              <div className="p-8 md:p-12 rounded-3xl bg-dark border border-dark-border" data-id="element-84">
                <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3" data-id="element-85">
                  <CheckCircle2 className="text-cyan h-8 w-8" data-id="element-86" />
                  Who this is FOR
                </h3>
                <ul className="space-y-6" data-id="element-87">
                  {['Adults diagnosed with ADHD or struggling with undiagnosed executive dysfunction.', 'Chronic procrastinators who are tired of the "shame cycle."', 'Solopreneurs and 9-5ers who feel constantly overwhelmed by "Life Admin."', 'Anyone who feels like their brain has too many tabs open at once.'].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-88">
                      <div className="h-2 w-2 rounded-full bg-cyan mt-2 flex-shrink-0" data-id="element-89" />
                      <span className="text-gray-300 leading-relaxed" data-id="element-90">
                        {text}
                      </span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-91">
              <div className="p-8 md:p-12 rounded-3xl bg-dark border border-dark-border opacity-80" data-id="element-92">
                <h3 className="text-2xl font-display font-bold mb-8 flex items-center gap-3" data-id="element-93">
                  <XCircle className="text-gray-500 h-8 w-8" data-id="element-94" />
                  Who this is NOT for
                </h3>
                <ul className="space-y-6" data-id="element-95">
                  {['People looking for a medical diagnosis or pharmaceutical advice.', 'Those who want a "magic pill" that requires zero engagement.', 'Anyone looking for a complex, 10-step productivity system (this is built for simplicity).'].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-96">
                      <div className="h-2 w-2 rounded-full bg-gray-600 mt-2 flex-shrink-0" data-id="element-97" />
                      <span className="text-gray-400 leading-relaxed" data-id="element-98">
                        {text}
                      </span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24" data-id="element-99">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-100">
          <FadeIn data-id="element-101">
            <div className="text-center mb-16" data-id="element-102">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-103">
                How It Works
              </h2>
              <p className="text-xl text-gray-400" data-id="element-104">
                Three simple steps to bypass the wall of awful.
              </p>
            </div>
          </FadeIn>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-8 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-dark-border before:to-transparent" data-id="element-105">
            {[{
            step: '1',
            title: 'The Brain Dump',
            desc: 'You feed your mental chaos into the AI using our specific templates.'
          }, {
            step: '2',
            title: 'The Energy Sort',
            desc: 'The system reorders your day based on your current mental bandwidth.'
          }, {
            step: '3',
            title: 'The Micro-Win',
            desc: 'You follow the AI-generated "Impossible-to-Fail" first step and build momentum.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-106">
                <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-107">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full border-4 border-dark bg-dark-card text-cyan font-display font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_0_4px_rgba(0,229,199,0.1)] z-10" data-id="element-108">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-5rem)] md:w-[calc(50%-3rem)] p-6 rounded-2xl bg-dark-card border border-dark-border" data-id="element-109">
                    <h4 className="text-xl font-display font-bold mb-2" data-id="element-110">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-111">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-24 bg-cyan/5 border-y border-cyan/10" data-id="element-112">
        <div className="container mx-auto px-6 max-w-3xl text-center" data-id="element-113">
          <FadeIn data-id="element-114">
            <ShieldCheck className="h-16 w-16 text-cyan mx-auto mb-8" data-id="element-115" />
            <blockquote className="text-2xl md:text-3xl font-display font-medium leading-relaxed mb-8" data-id="element-116">
              "Within 7 days of using the Brain-Sync System, users report a{' '}
              <span className="text-cyan" data-id="element-117">'quieting'</span> of mental noise."
            </blockquote>
            <p className="text-xl text-gray-400 leading-relaxed" data-id="element-118">
              The primary shift isn't just getting more done—it's the
              elimination of the 11:00 PM anxiety that usually comes from a day
              of "not doing enough."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" data-id="element-119">
        <div className="container mx-auto px-6" data-id="element-120">
          <FadeIn data-id="element-121">
            <div className="text-center mb-16" data-id="element-122">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-123">
                Frequently Asked Questions
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-124">
            <FAQAccordion items={[{
            question: 'Do I need a paid AI subscription?',
            answer: 'No, the free versions of ChatGPT or Claude work perfectly with these prompts.'
          }, {
            question: 'How long does it take to learn?',
            answer: 'You can implement the first "Bypass" prompt in under 5 minutes.'
          }, {
            question: 'Is this just for work?',
            answer: 'No, it includes modules for meal planning, emails, and cleaning your house.'
          }, {
            question: 'What if I forget to use it for a few days?',
            answer: 'The system is built for that. There is an "I Fell Off the Wagon" prompt specifically for restarts.'
          }]} data-id="element-125" />
          </FadeIn>
        </div>
      </section>

      {/* PRICING / FINAL CTA */}
      <section id="pricing" className="py-32 relative overflow-hidden" data-id="element-126">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-full bg-cyan/10 blur-[150px] rounded-full pointer-events-none" data-id="element-127" />

        <div className="container mx-auto px-6 max-w-3xl relative z-10 text-center" data-id="element-128">
          <FadeIn data-id="element-129">
            <div className="p-8 md:p-12 rounded-3xl bg-dark-card border border-dark-border shadow-2xl shadow-cyan/5" data-id="element-130">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-131">
                Reclaim your focus
              </h2>
              <p className="text-xl text-gray-400 mb-8" data-id="element-132">
                End the "Executive Dysfunction Tax" today. Get the complete
                Brain-Sync System, including the PDF Guide and the 50-Prompt
                Matrix.
              </p>

              <div className="mb-10" data-id="element-133">
                <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-cyan/10 border border-cyan/20 mb-4" data-id="element-134">
                  <span className="text-cyan font-bold text-sm uppercase tracking-wider" data-id="element-135">
                    52% OFF
                  </span>
                </div>
                <div className="flex items-center justify-center gap-4" data-id="element-136">
                  <span className="text-2xl font-display text-gray-500 line-through" data-id="element-137">
                    $47.00
                  </span>
                  <span className="text-6xl font-display font-bold text-white" data-id="element-138">
                    $22<span className="text-3xl text-gray-500" data-id="element-139">.00</span>
                  </span>
                </div>
              </div>

              <CTAButton className="w-full sm:w-auto mb-6" onClick={() => {
              const fbq = (window as any).fbq;
              if (fbq) {
                fbq('track', 'InitiateCheckout', {
                  content_name: 'Brain-Sync System',
                  content_type: 'product',
                  value: 22.0,
                  currency: 'USD'
                });
              }
              window.open('https://thenexusaihub.com//cart/48342518366463:1', '_blank');
            }} data-id="element-140">
                GET THE BRAIN-SYNC SYSTEM NOW
              </CTAButton>

              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500" data-id="element-141">
                <span className="flex items-center gap-1" data-id="element-142">
                  <CheckCircle2 className="h-4 w-4 text-cyan" data-id="element-143" /> Download
                  instantly
                </span>
                <span className="flex items-center gap-1" data-id="element-144">
                  <CheckCircle2 className="h-4 w-4 text-cyan" data-id="element-145" /> One-time
                  payment
                </span>
                <span className="flex items-center gap-1" data-id="element-146">
                  <CheckCircle2 className="h-4 w-4 text-cyan" data-id="element-147" /> Lifetime peace
                  of mind
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 border-t border-dark-border text-center text-sm" data-id="element-148">
        <div className="container mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-149">
          <p className="text-gray-600" data-id="element-150">
            © {new Date().getFullYear()} The Brain-Sync System. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-151">
            <button onClick={() => setShowPrivacy(true)} className="text-gray-500 hover:text-cyan transition-colors focus:outline-none focus:underline" data-id="element-152">
              Privacy Policy
            </button>
            <button onClick={() => setShowTerms(true)} className="text-gray-500 hover:text-cyan transition-colors focus:outline-none focus:underline" data-id="element-153">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-154">
        <PrivacyContent data-id="element-155" />
      </LegalModal>
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-156">
        <TermsContent data-id="element-157" />
      </LegalModal>
    </div>;
}